import { Checkbox, Modal, Typography } from '@a_team/ui-components';
import { useAnalytics } from '@ateams/analytics/dist/platform';
import { Button } from '@ateams/components';
import {
  PrivacyPolicyLocation,
  RootLocation,
  TermsOfServiceLocation,
} from '@src/locations';
import { useStores } from '@src/stores';
import React, { ReactElement, useState } from 'react';
import { createUseStyles } from 'react-jss';

const useStyles = createUseStyles({
  container: {
    background: 'rgba(0, 0, 0, 0.5)',
    height: '100vh',
    width: '100vw',
  },
  modal: {
    minWidth: '480px',
    padding: '8px',
    borderRadius: '12px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
  },
  title: {
    '&&': {
      marginTop: 0,
      marginBottom: '16px',
      textAlign: 'center',
      fontSize: '20px',
      fontWeight: 600,
    },
  },
  text: {
    '&&': {
      fontWeight: 400,
      fontSize: '16px',
      textAlign: 'center',
      display: 'block',
      marginBottom: '8px',
    },
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: '16px',
    width: '100%',
    marginTop: '24px',
    '& button': {
      borderRadius: '6px',
      fontSize: '16px',
      width: 'auto',
    },
  },
  signOutButton: {
    padding: '8px 16px',
  },
  agreeButton: {
    padding: '8px 40px',
  },
});

export const AcceptNewTOS = ({
  tosAlreadyAccepted,
}: {
  tosAlreadyAccepted: boolean;
}): ReactElement => {
  const styles = useStyles();
  const { auth } = useStores();
  const analytics = useAnalytics();
  const [accepted, setAccepted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const signOut = () => {
    analytics.trackSignOut(auth.user);
    auth.logout(true).then(() => {
      window.location.replace(RootLocation);
    });
  };

  const agreeToNewTOS = () => {
    setIsLoading(true);
    auth.acceptTOSV2(auth).finally(() => {
      setIsLoading(false);
    });
  };

  return (
    <div className={styles.container}>
      <Modal
        isOpen
        hideCloseButton
        shouldHideGradientStroke
        className={styles.modal}
      >
        <Typography variant="h3" className={styles.title}>
          {tosAlreadyAccepted
            ? 'We’ve updated our Platform Terms of Service and Privacy Policy'
            : 'Terms of Service'}
        </Typography>
        <Typography variant="textMedium" className={styles.text}>
          By clicking "I agree", you agree to our
          {tosAlreadyAccepted ? ' updated' : ''}{' '}
          <a
            href={TermsOfServiceLocation}
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms of Service agreement
          </a>{' '}
          and{' '}
          <a
            href={PrivacyPolicyLocation}
            target="_blank"
            rel="noopener noreferrer"
          >
            A.Teamer Privacy Statement
          </a>
          .
        </Typography>
        <div>
          <Checkbox
            checked={accepted}
            onChange={() => setAccepted(!accepted)}
            label={
              tosAlreadyAccepted
                ? 'I have read and agree to the updates.'
                : 'I have reviewed and accept the terms and policy.'
            }
            color="Grey@800"
          />
        </div>
        <div className={styles.buttonContainer}>
          {tosAlreadyAccepted && (
            <Button
              color="backgroundDark"
              size="small"
              className={styles.signOutButton}
              onClick={signOut}
            >
              Sign out
            </Button>
          )}
          <Button
            color="secondaryDark"
            size="small"
            className={styles.agreeButton}
            onClick={agreeToNewTOS}
            loading={isLoading}
            disabled={!accepted}
          >
            I agree
          </Button>
        </div>
      </Modal>
    </div>
  );
};
